import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AuthCallback from "./pages/AuthCallback";
import PaymentSuccess from "./pages/PaymentSuccess";
import PricingPage from "./pages/PricingPage";
import UpgradeListener from "./components/UpgradeListener";
import TeacherLayout from "./teacher/TeacherLayout";
import TeacherOverviewPage from "./teacher/TeacherOverviewPage";
import TeacherClassesPage from "./teacher/TeacherClassesPage";
import TeacherClassroomDetailPage from "./teacher/TeacherClassroomDetailPage";
import TeacherStudentProfilePage from "./teacher/TeacherStudentProfilePage";
import { TeacherSubmissionsPage, TeacherSubmissionDetailPage } from "./teacher/TeacherSubmissionsPage";
import TeacherAnalyticsPage from "./teacher/TeacherAnalyticsPage";
import TeacherSettingsPage from "./teacher/TeacherSettingsPage";
import TeacherQuestionBankPage from "./teacher/TeacherQuestionBankPage";
import TeacherCreatePage from "./teacher/TeacherCreatePage";
import TeacherMarkPage from "./teacher/TeacherMarkPage";
import { Toaster } from "sonner";

// Public route that redirects to dashboard if already logged in
function PublicRoute({ children }) {
    const { user, loading } = useAuth();
    
    if (loading) {
        return null;
    }
    
    if (user && user !== false) {
        // Admin defaults to /dashboard but can also access /teacher freely.
        const dest = user.role === 'teacher' ? '/teacher' : '/dashboard';
        return <Navigate to={dest} replace />;
    }
    
    return children;
}

// Route gate: teachers + admin only — redirects students to /dashboard
function TeacherOnlyRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user || user === false) return <Navigate to="/login" replace />;
    if (user.role !== 'teacher' && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
    return children;
}

function AppRoutes() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Detect Emergent Google Auth callback synchronously — before ProtectedRoute runs.
    // The session_id arrives in the URL fragment (not query params) at any route.
    const location = useLocation();
    if (location.hash && location.hash.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/register" 
                element={
                    <PublicRoute>
                        <RegisterPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/forgot-password" 
                element={
                    <PublicRoute>
                        <ForgotPasswordPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/reset-password" 
                element={
                    <PublicRoute>
                        <ResetPasswordPage />
                    </PublicRoute>
                } 
            />
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin" 
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                } 
            />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Teacher LMS (role-gated) */}
            <Route path="/teacher" element={<TeacherOnlyRoute><TeacherLayout /></TeacherOnlyRoute>}>
                <Route index element={<TeacherOverviewPage />} />
                <Route path="classes" element={<TeacherClassesPage />} />
                <Route path="classes/:classroomId" element={<TeacherClassroomDetailPage />} />
                <Route path="classes/:classroomId/students/:studentId" element={<TeacherStudentProfilePage />} />
                <Route path="submissions" element={<TeacherSubmissionsPage />} />
                <Route path="submissions/:submissionId" element={<TeacherSubmissionDetailPage />} />
                <Route path="question-bank" element={<TeacherQuestionBankPage />} />
                <Route path="create" element={<TeacherCreatePage />} />
                <Route path="mark" element={<TeacherMarkPage />} />
                <Route path="analytics" element={<TeacherAnalyticsPage />} />
                <Route path="settings" element={<TeacherSettingsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}
function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster position="top-right" richColors />
                <UpgradeListener />
                <AppRoutes />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
